import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import DomainsPanel from './components/DomainsPanel';
import UseCasesPanel from './components/UseCasesPanel';
import DetailsPanel from './components/DetailsPanel';
import AgentModal from './components/AgentModal';
import TabModal from './components/TabModal';
import {
    fetchDomains as apiFetchDomains,
    fetchUseCases as apiFetchUseCases,
    fetchTabs as apiFetchTabs,
    fetchAgents as apiFetchAgents,
    fetchTools as apiFetchTools,
    fetchToolConfig as apiFetchToolConfig,
    fetchMaxDomainId as apiFetchMaxDomainId,
    fetchMaxUseCaseId as apiFetchMaxUseCaseId,
    fetchMaxTabId as apiFetchMaxTabId,
    addDomain,
    createUseCase,
    runUpdate,
    createTab,
    createAgent,
} from './api';
const incrementId = (currentId) => {
    if (!currentId || typeof currentId !== 'string' || !/^\d+$/.test(currentId)) {
        return '00001';
    }
    const num = parseInt(currentId, 10) + 1;
    return num.toString().padStart(5, '0');
};

const generateUniqueAgentID = () => {
    const timestamp = Date.now();
    const id = timestamp.toString().slice(-5);
    const randomLetter = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    return randomLetter + id.slice(-4);
};

const CustomBuild = () => {
    const [domains, setDomains] = useState([]);
    const [selectedDomainId, setSelectedDomainId] = useState(null);

    const [useCases, setUseCases] = useState([]);
    const [selectedUseCaseId, setSelectedUseCaseId] = useState(null);

    const [tabs, setTabs] = useState([]);
    const [selectedTabId, setSelectedTabId] = useState(null);

    const [agents, setAgents] = useState([]);

    const [tools, setTools] = useState([]);
    const [selectedToolId, setSelectedToolId] = useState(null);
    const [toolPrompt, setToolPrompt] = useState('');
    const [additionalToolPrompt, setAdditionalToolPrompt] = useState('');
    const [toolConfig, setToolConfig] = useState(null);

    const [showAgentModal, setShowAgentModal] = useState(false);
    const [showTabModal, setShowTabModal] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [editingTab, setEditingTab] = useState(null);

    const [userId, setUserId] = useState(null);
    useEffect(() => {
        const storedUserId = sessionStorage.getItem('user_id');
        setUserId(storedUserId || 'U1757535255');
    }, []);

    const [agentForm, setAgentForm] = useState({
        name: '',
        tool_id: '',
        tool_prompt: '',
        status: 'inactive',
        visual_prompt: '',
        widget_id: '',
        user_id: userId,
    });

    const [tabForm, setTabForm] = useState({
        name: '',
        description: '',
        use_case_id: '',
        userId: userId,
    });

    const [formData, setFormData] = useState({
        id: '',
        title: '',
        domain: '',
        createdBy: '',
        description: '',
    });

    const navigate = useNavigate();

    useEffect(() => {
        loadDomains();
    }, []);

    const loadDomains = async () => {
        try {
            const data = await apiFetchDomains();
            setDomains(data);
        } catch (err) {
            console.error('Error loading domains:', err);
        }
    };

    const loadUseCases = async (domainId) => {
        try {
            const data = await apiFetchUseCases(domainId);
            setUseCases(data);
        } catch (err) {
            console.error('Error loading use cases:', err);
        }
    };

    const loadTabs = async (useCaseId) => {
        try {
            const data = await apiFetchTabs(useCaseId, userId);
            setTabs(data);
        } catch (err) {
            console.error('Error loading tabs:', err);
        }
    };

    const loadAgents = async (tabId) => {
        try {
            const data = await apiFetchAgents(tabId, userId);
            setAgents(data);
        } catch (err) {
            console.error('Error loading agents:', err);
        }
    };

    const handleSelectDomain = (idx) => {
        setSelectedDomainId(idx);
        const domainId = domains[idx]?.id;
        if (domainId) loadUseCases(domainId);
        setUseCases([]);
        setSelectedUseCaseId(null);
        setTabs([]);
        setSelectedTabId(null);
        setAgents([]);
    };

    const handleAddDomain = async () => {
        const newName = prompt('Enter new domain name');
        if (!newName) return;
        try {
            const maxId = await apiFetchMaxDomainId();
            const newId = incrementId(maxId);
            const ok = await addDomain(String(newId).padStart(5, '0'), newName.substring(0, 255));
            if (ok) loadDomains();
        } catch (err) {
            console.error('Failed to add domain:', err);
        }
    };

    const handleSelectUseCase = (idx) => {
        setSelectedUseCaseId(idx);
        const selected = useCases[idx];
        setFormData({
            id: selected.id,
            title: selected.name,
            domain: selected.domain || '',
            createdBy: selected.createdBy || '',
            description: selected.description || '',
        });
        loadTabs(selected.id);
        setSelectedTabId(null);
        setAgents([]);
    };

    const handleAddUseCase = () => {
        if (selectedDomainId === null && selectedDomainId !== 0) {
            alert('Please select a domain first');
            return;
        }
        const domain = domains[selectedDomainId];
        if (!domain) {
            alert('Please select a domain first');
            return;
        }
        setUseCases([...useCases, {
            id: null,
            name: 'New Use Case',
            domain: domain?.name || '',
            description: '',
            createdBy: '',
        }]);
        setTabs([]);
        setSelectedTabId(null);
        setAgents([]);
        setSelectedUseCaseId(useCases.length);
        setFormData({
            title: 'New Use Case',
            domain: domain?.name || '',
            createdBy: '',
            description: '',
        });
    };

    const handleUseCaseSave = async () => {
        try {
            if (selectedDomainId === null && selectedDomainId !== 0) {
                alert('Please select a domain');
                return;
            }
            const domain = domains[selectedDomainId];
            if (!domain) {
                alert('Domain not found');
                return;
            }
            if (!formData.id) {
                const newID = await apiFetchMaxUseCaseId();
                const newUseCaseId = incrementId(newID);
                const ok = await createUseCase({
                    id: newUseCaseId,
                    name: formData.title,
                    description: formData.description,
                    domain_id: domain.id,
                    created_by: formData.createdBy,
                });
                if (ok) {
                    toast.success('Use Case Saved Successfully');
                    await loadUseCases(domain.id);
                    setSelectedUseCaseId(null);
                }
            } else {
                const ok = await runUpdate(
                    `UPDATE use_cases SET name='${formData.title}',description='${formData.description}',created_by='${formData.createdBy}' where id='${formData.id}'`
                );
                if (ok) {
                    toast.success('Use Case Saved Successfully');
                    await loadUseCases(domain.id);
                } else {
                    toast.error('Error while saving the Use Case');
                }
            }
        } catch (err) {
            console.error('Error saving use case:', err);
        }
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setUseCases(prev => prev.map((uc, idx) =>
            idx === selectedUseCaseId
                ? { ...uc, [field === 'title' ? 'name' : field]: value }
                : uc
        ));
    };

    const handleSelectTab = (idx) => {
        setSelectedTabId(idx);
        const selected = tabs[idx];
        if (selected?.id) loadAgents(selected.id);
    };

    const handleAddTab = () => {
        const useCase = useCases[selectedUseCaseId];
        if (!useCase?.id) {
            toast.error('Please save the use case first');
            return;
        }
        setTabForm({ name: 'New Tab', description: '', use_case_id: useCase.id });
        setEditingTab(null);
        setShowTabModal(true);
    };

    const handleTabSave = async () => {
        try {
            if (!tabForm.name) { toast.error('Tab name is required'); return; }
            if (!tabForm.use_case_id) { toast.error('No use case selected'); return; }

            if (editingTab) {
                const ok = await runUpdate(
                    `UPDATE tabs SET name='${tabForm.name}', description='${tabForm.description || ''}' WHERE id='${editingTab.id}'`
                );
                if (ok) {
                    toast.success('Tab updated successfully');
                    loadTabs(tabForm.use_case_id);
                } else {
                    toast.error('Error updating tab');
                }
            } else {
                const maxId = await apiFetchMaxTabId();
                const newTabId = incrementId(maxId);
                const ok = await createTab({
                    id: newTabId,
                    name: tabForm.name,
                    use_case_id: tabForm.use_case_id,
                    user_id: userId,
                });
                if (ok) {
                    toast.success('Tab created successfully');
                    loadTabs(tabForm.use_case_id);
                } else {
                    toast.error('Error creating tab');
                }
            }
            closeTabModal();
        } catch (err) {
            console.error('Error saving tab:', err);
            toast.error('Error saving tab');
        }
    };

    const openAgentModal = async (agent = null) => {
        const toolsData = await apiFetchTools();
        setTools(toolsData);

        if (agent) {
            setToolPrompt(agent.tool_prompt || '');
            setAdditionalToolPrompt(agent.visual_prompt || '');
            setSelectedToolId(agent.tool_id || null);
            setAgentForm({
                name: agent.name,
                tool_id: agent.tool_id || '',
                tool_prompt: agent.tool_prompt || '',
                status: agent.status || 'inactive',
                visual_prompt: agent.visual_prompt || '',
                widget_id: agent.widget_id || '',
                user_id: agent.user_id || '00001',
            });
            setEditingAgent(agent);
            if (agent.tool_id) {
                const config = await apiFetchToolConfig(agent.tool_id);
                setToolConfig(config);
            }
        } else {
            setToolPrompt('');
            setAdditionalToolPrompt('');
            setSelectedToolId(null);
            setToolConfig(null);
            setAgentForm({
                name: '',
                tool_id: '',
                tool_prompt: '',
                status: 'inactive',
                visual_prompt: '',
                widget_id: '',
                user_id: '00001',
            });
            setEditingAgent(null);
        }
        setShowAgentModal(true);
    };

    const closeAgentModal = () => {
        setShowAgentModal(false);
        setEditingAgent(null);
        setToolConfig(null);
    };

    const openTabModal = (tab = null) => {
        if (tab) {
            setTabForm({ name: tab.name, description: tab.description || '', use_case_id: tab.use_case_id });
            setEditingTab(tab);
        } else {
            const useCase = useCases[selectedUseCaseId];
            if (!useCase?.id) { toast.error('Please save the use case first'); return; }
            setTabForm({ name: 'New Tab', description: '', use_case_id: useCase.id });
            setEditingTab(null);
        }
        setShowTabModal(true);
    };

    const closeTabModal = () => {
        setShowTabModal(false);
        setEditingTab(null);
    };

    const handleAgentFormChange = (field, value) => {
        setAgentForm(prev => ({ ...prev, [field]: value }));
    };

    const handleTabFormChange = (field, value) => {
        setTabForm(prev => ({ ...prev, [field]: value }));
    };

    const handleToolSelection = async (toolId) => {
        setSelectedToolId(toolId);
        handleAgentFormChange('tool_id', toolId);
        const config = await apiFetchToolConfig(toolId);
        setToolConfig(config);
    };

    const saveAgent = async () => {
        try {
            if (selectedTabId === null && selectedTabId !== 0) {
                alert('Please select a tab first');
                return;
            }
            const currentTab = tabs[selectedTabId];
            if (!currentTab?.id) {
                alert('Please save the tab first');
                return;
            }
            const agentData = {
                name: agentForm.name,
                tool_id: selectedToolId || null,
                tool_prompt: toolPrompt,
                visual_prompt: agentForm.status === 'active' ? additionalToolPrompt : '',
                user_id: userId,
                tab_id: currentTab.id,
            };

            if (editingAgent) {
                const ok = await runUpdate(
                    `UPDATE widgets SET name='${agentData.name}', tool_id='${agentData.tool_id}',tool_prompt='${agentData.tool_prompt}',visual_prompt='${agentData.visual_prompt}', user_id='${agentData.user_id}', tab_id='${agentData.tab_id}' where id='${editingAgent.id}'`
                );
                if (ok) {
                    toast.success('Agent updated successfully');
                    loadAgents(currentTab.id);
                } else {
                    toast.error('Error Updating Agent');
                }
            } else {
                const newAgentId = generateUniqueAgentID();
                const ok = await createAgent({ ...agentData, id: newAgentId });
                if (ok) {
                    toast.success('Agent created successfully');
                    loadAgents(currentTab.id);
                } else {
                    toast.error('Error creating agent');
                }
            }
            closeAgentModal();
        } catch (err) {
            console.error('Error saving agent:', err);
            toast.error('Error saving agent');
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-100">
            <div className="flex-none bg-gray-100 px-4 sm:px-6 shadow-sm z-20">
                <div className="pt-4 pb-3 border-b border-gray-300">
                    <h1 className="text-2xl font-bold text-primary">Agent Factory</h1>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-stretch h-[calc(100vh-160px)]">
                        <DomainsPanel
                            domains={domains}
                            selectedDomainId={selectedDomainId}
                            onSelectDomain={handleSelectDomain}
                            onAddDomain={handleAddDomain}
                        />
                        <UseCasesPanel
                            useCases={useCases}
                            selectedUseCaseId={selectedUseCaseId}
                            selectedDomainId={selectedDomainId}
                            onSelectUseCase={handleSelectUseCase}
                            onAddUseCase={handleAddUseCase}
                            onNavigateToUseCase={(id) => navigate(`/use-case/${id}`)}
                        />
                        <DetailsPanel
                            selectedUseCaseId={selectedUseCaseId}
                            formData={formData}
                            tabs={tabs}
                            selectedTabId={selectedTabId}
                            agents={agents}
                            onFormChange={handleFormChange}
                            onUseCaseSave={handleUseCaseSave}
                            onAddTab={handleAddTab}
                            onSelectTab={handleSelectTab}
                            onOpenTabModal={openTabModal}
                            onAddAgent={() => openAgentModal(null)}
                            onOpenAgentModal={openAgentModal}
                        />
                    </div>
            </div>

            <AgentModal
                show={showAgentModal}
                editingAgent={editingAgent}
                tools={tools}
                selectedToolId={selectedToolId}
                toolPrompt={toolPrompt}
                additionalToolPrompt={additionalToolPrompt}
                agentForm={agentForm}
                toolConfig={toolConfig}
                onClose={closeAgentModal}
                onSave={saveAgent}
                onAgentFormChange={handleAgentFormChange}
                onToolSelection={handleToolSelection}
                onToolPromptChange={setToolPrompt}
                onAdditionalToolPromptChange={setAdditionalToolPrompt}
            />

            <TabModal
                show={showTabModal}
                editingTab={editingTab}
                tabForm={tabForm}
                onClose={closeTabModal}
                onSave={handleTabSave}
                onTabFormChange={handleTabFormChange}
            />
        </div>
    );
};

export default CustomBuild;
